import { useLayoutEffect, useSyncExternalStore } from "react";

import { DEFAULT_COLLAPSED_SECTIONS, sectionIdForPath } from "@/lib/nav";

/**
 * Which sidebar groups this admin has collapsed.
 *
 * Same shape as `recent-users.ts` — a module-level parse cache plus a change
 * event, read through useSyncExternalStore — rather than component state,
 * because the nav is mounted twice at once (the desktop rail, which stays
 * mounted behind `hidden md:flex`, and the mobile Sheet) and both copies must
 * agree. The `storage` listener keeps a second tab in step too.
 *
 * We persist the COLLAPSED ids, not an open/closed map: a group added later
 * defaults to open — the safe direction, nothing hides by surprise — and an
 * id whose group was deleted is inert garbage rather than a stale `false`.
 * Before the admin has expressed any preference there is no stored value at
 * all, so `DEFAULT_COLLAPSED_SECTIONS` stands in.
 */
const STORAGE_KEY = "moreta.admin.navCollapsed";
const CHANGE_EVENT = "moreta:nav-collapsed";

let cache: { raw: string | null; parsed: string[] } | null = null;

const readCollapsed = (): string[] => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage disabled (private window, blocked site data) — defaults apply.
    return DEFAULT_COLLAPSED_SECTIONS;
  }
  // useSyncExternalStore needs a referentially stable snapshot while the
  // stored value is unchanged, so parse once per distinct raw string.
  if (cache && raw === cache.raw) return cache.parsed;

  let parsed: string[] = DEFAULT_COLLAPSED_SECTIONS;
  if (raw !== null) {
    try {
      const value: unknown = JSON.parse(raw);
      parsed = Array.isArray(value)
        ? value.filter((id): id is string => typeof id === "string")
        : DEFAULT_COLLAPSED_SECTIONS;
    } catch {
      parsed = DEFAULT_COLLAPSED_SECTIONS;
    }
  }
  cache = { raw, parsed };
  return parsed;
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
};

export const setSectionCollapsed = (id: string, collapsed: boolean): void => {
  const current = readCollapsed();
  // Idempotent: a no-op write would still notify every subscriber, and both
  // mounted navs subscribe.
  if (current.includes(id) === collapsed) return;
  const next = collapsed
    ? [...current, id]
    : current.filter((entry) => entry !== id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full or disabled — a collapse preference is a convenience.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const useCollapsedSections = (): string[] =>
  useSyncExternalStore(
    subscribe,
    readCollapsed,
    () => DEFAULT_COLLAPSED_SECTIONS,
  );

/**
 * A collapsed group must never hide the page you are on, so navigating into
 * one expands it — and keeps it expanded, because you have just shown that
 * you use it.
 *
 * Deliberately a layout effect: an ordinary effect runs after paint, which
 * would show one frame of the collapsed group before it springs open. And
 * deliberately not a force-open during render, which would leave the group's
 * own header looking like a dead button while you stand inside it.
 */
export const useAutoExpandActiveSection = (pathname: string): void => {
  useLayoutEffect(() => {
    const active = sectionIdForPath(pathname);
    if (active !== null) setSectionCollapsed(active, false);
  }, [pathname]);
};
